/**
 * DailyReconcileBox — 30-second daily supplement.
 *
 * Shows what auto-capture saw today and lets the rep add anything that was
 * missed (calls outside the dialer, in-person meetings, etc.). Writes a
 * `source='self'` row to /api/me/activity/daily; the API blends auto + self
 * into displayed totals.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/session";

interface AutoSummary {
  outreach: number;
  first_meetings: number;
  qualified_meetings: number;
  closed_won_count: number;
  qualified_handoffs: number;
}

const ZERO: AutoSummary = {
  outreach: 0,
  first_meetings: 0,
  qualified_meetings: 0,
  closed_won_count: 0,
  qualified_handoffs: 0,
};

export interface DailyReconcileBoxProps {
  bdrMode?: boolean;
  onSaved?: () => void;
}

export function DailyReconcileBox({ bdrMode = false, onSaved }: DailyReconcileBoxProps) {
  const today = new Date().toISOString().slice(0, 10);
  const [auto, setAuto] = useState<AutoSummary>(ZERO);
  const [delta, setDelta] = useState<AutoSummary>(ZERO);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api<{ auto: AutoSummary }>(`/api/me/activity?from=${today}&to=${today}`);
        if (!cancelled && r?.auto) setAuto({ ...ZERO, ...r.auto });
      } catch { /* tolerate */ }
    })();
    return () => { cancelled = true; };
  }, [today]);

  async function save() {
    setSaving(true);
    try {
      await api("/api/me/activity/daily", {
        method: "PUT",
        body: JSON.stringify({
          activity_date: today,
          ...delta,
          notes: notes || null,
        }),
      });
      setSavedAt(new Date().toLocaleTimeString("en-US", { timeZone: "America/Detroit", hour: "2-digit", minute: "2-digit" }));
      setDelta(ZERO);
      setNotes("");
      onSaved?.();
    } finally { setSaving(false); }
  }

  function fld(key: keyof AutoSummary, label: string) {
    return (
      <label className="flex flex-col text-xs text-neutral-700">
        <span className="mb-1">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-neutral-500">auto {auto[key]}</span>
          <input
            type="number"
            min={0}
            className="w-20 rounded border border-neutral-300 px-2 py-1 text-right text-sm"
            value={delta[key]}
            onChange={(e) => setDelta({ ...delta, [key]: Number(e.target.value || 0) })}
            data-testid={`reconcile-${String(key)}`}
          />
        </div>
      </label>
    );
  }

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-medium" style={{ color: "#0B0B0F", fontFamily: "DM Sans, sans-serif" }}>
          Anything we missed today?
        </h3>
        <span className="text-[11px] text-neutral-500">30-second supplement, optional.</span>
      </div>
      <p className="mb-3 text-xs text-neutral-600">
        Auto-captured today: outreach {auto.outreach}, first meetings {auto.first_meetings}, qual {auto.qualified_meetings}, closes {auto.closed_won_count}
        {bdrMode ? `, handoffs ${auto.qualified_handoffs}` : ""}.
      </p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {fld("outreach", "Outreach")}
        {fld("first_meetings", "First meetings")}
        {fld("qualified_meetings", "Qualified meetings")}
        {fld("closed_won_count", "Closes")}
        {bdrMode && fld("qualified_handoffs", "Handoffs")}
      </div>
      <textarea
        placeholder="Anything else to note? (optional)"
        className="mt-3 w-full rounded border border-neutral-300 p-2 text-sm"
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        data-testid="reconcile-notes"
      />
      <div className="mt-3 flex items-center justify-between">
        <span className="text-[11px] text-neutral-500">{savedAt ? `Saved at ${savedAt}` : ""}</span>
        <button
          className="rounded bg-[#DC2626] px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          onClick={save}
          disabled={saving}
          data-testid="reconcile-save"
        >{saving ? "Saving..." : "Save"}</button>
      </div>
    </div>
  );
}

export default DailyReconcileBox;
